import { byCssHtml, closestElement, toHtmlElements } from "@de-xima/xima-common-js-dom";
import { assertInstance, isEmpty, isNullish, parseString, substringBeforeLast } from "@de-xima/xima-common-js-lang";

import { findWidgetForElement } from "./Util.ts";

export function onAddNewLanguageClicked(event: Event): boolean {
  event.stopPropagation();
  event.preventDefault();

  const dom = acquireDomForEvent(event);

  // biome-ignore lint/complexity/useLiteralKeys: We need to access a private method
  dom.newLanguage["clearCache"]();
  dom.newLanguage.clear();

  dom.overlayPanel.show(dom.buttonAdd.jq);
  setTimeout(() => dom.newLanguage.input.get(0)?.focus());

  return false;
}

export function onNewLanguageInputKeyDown(event: Event): boolean {
  if (event instanceof KeyboardEvent && event.key === "Enter") {
    const dom = acquireDomForEvent(event);
    if (!dom.newLanguage.panel.is(":visible")) {
      event.stopPropagation();
      event.preventDefault();
      dom.buttonOk.jq.get(0)?.click();
      return false;
    }
  }
  return true;
}

export function onNewLanguageOkClicked(event: Event): boolean {
  event.stopPropagation();
  event.preventDefault();

  const dom = acquireDomForEvent(event);

  if (isEmpty(parseString(dom.newLanguage.input.val()))) {
    dom.newLanguage.jq.effect("shake");
  }

  return true;
}

export function onDeleteLanguageClicked(event: Event): boolean {
  event.stopPropagation();
  return true;
}

export function onNewLanguageOkAjaxComplete(settings: PrimeFaces.ajax.Configuration): void {
  const element =
    typeof settings.source === "string" ? document.getElementById(settings.source) : toHtmlElements(settings.source)[0];
  if (isNullish(element)) {
    console.error("LocalizationTabs: Unable to find source element");
    return;
  }

  const dom = acquireDomForElement(element);
  dom.newLanguage.clear();
  dom.overlayPanel.hide();
}

export function onNewLanguageCancelClicked(event: Event): boolean {
  event.stopPropagation();
  event.preventDefault();

  const dom = acquireDomForEvent(event);

  dom.newLanguage.clear();
  dom.overlayPanel.hide();

  return false;
}

interface Dom {
  buttonAdd: PrimeFaces.widget.CommandButton;
  buttonOk: PrimeFaces.widget.CommandButton;
  newLanguage: PrimeFaces.widget.AutoComplete;
  overlayPanel: PrimeFaces.widget.OverlayPanel;
}

function acquireDomForEvent(event: Event): Dom {
  const element = event.target;
  assertInstance(element, HTMLElement);
  return acquireDomForElement(element);
}

function acquireDomForElement(element: HTMLElement): Dom {
  const container = closestElement(element, ".localization-tabs,.localization-tabs__add-panel");
  if (container === undefined) {
    throw new Error("LocalizationTabs: Unable to find container");
  }

  const baseId = substringBeforeLast(container.id, ":");

  const panelElement = document.getElementById(`${baseId}:newLanguagePanel`);
  const tabsElement = document.getElementById(`${baseId}:tabs`);
  if (panelElement === null || tabsElement === null) {
    throw new Error("LocalizationTabs: Unable to find new language /tabs panel");
  }

  const buttonAdd = findWidgetForElement(
    byCssHtml(".localization-tabs__add-button", tabsElement),
    PrimeFaces.widget.CommandButton,
  );

  const overlayPanel = findWidgetForElement(panelElement, PrimeFaces.widget.OverlayPanel);

  const buttonOk = findWidgetForElement(
    byCssHtml(".localization-tabs__ok-button", panelElement),
    PrimeFaces.widget.CommandButton,
  );

  const newLanguage = findWidgetForElement(
    byCssHtml(".localization-tabs__new-language", panelElement),
    PrimeFaces.widget.AutoComplete,
  );

  if (buttonAdd === undefined || buttonOk === undefined || newLanguage === undefined || overlayPanel === undefined) {
    throw new Error("LocalizationTabs: Unable to find required elements");
  }

  return { buttonAdd, buttonOk, newLanguage, overlayPanel };
}
